import { ApiClientError, api, unwrap } from "@lumberset/api-client";
import { bpsToPercent, date, money, quantity, unitLabel } from "@lumberset/takeoff-ui";
import { Button, EmptyState, Prompt, StatusBadge, toast } from "@lumberset/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useMemo, useState } from "react";

/** The Quote as the Customer sees it. No sign-in: knowing the link is the permission. */
export const Route = createFileRoute("/q/$token")({ component: PublicQuote });

const sectionTitles: Record<string, string> = {
  walls: "Wall framing",
  floors: "Floor and ceiling framing",
  roof: "Roof framing",
  beams_posts: "Beams and posts",
  sheathing: "Sheathing and subfloor",
  hardware: "Hardware",
  other: "Other",
};

function PublicQuote() {
  const { token } = Route.useParams();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<"accepted" | "declined" | null>(null);

  const { data, error, isPending } = useQuery({
    queryKey: ["public-quote", token],
    queryFn: () => unwrap(api.public.quotes[":token"].$get({ param: { token } })),
    retry: false,
  });

  const decide = useMutation({
    mutationFn: (decision: "accepted" | "declined") =>
      unwrap(api.public.quotes[":token"].decision.$post({ param: { token }, json: { decision } })),
    onSuccess: async ({ status }) => {
      await queryClient.invalidateQueries({ queryKey: ["public-quote", token] });
      toast.success(
        status === "accepted" ? "Quote accepted. The yard has been told." : "Quote declined.",
      );
    },
    onError: (failure) => toast.error(failure.message),
  });

  const sections = useMemo(() => {
    const grouped = new Map<string, NonNullable<typeof data>["lines"]>();
    for (const line of data?.lines ?? [])
      grouped.set(line.section, [...(grouped.get(line.section) ?? []), line]);
    return [...grouped];
  }, [data]);

  if (isPending) return <div className="min-h-dvh bg-ui-bg-subtle" />;
  if (error || !data) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-ui-bg-subtle">
        <EmptyState
          title="This quote is not available"
          description={
            error instanceof ApiClientError && error.status !== 404
              ? error.message
              : "The link may be mistyped, or the yard has withdrawn the quote. Ask them for a new link."
          }
        />
      </div>
    );
  }

  const { yard, quote } = data;
  const open = quote.status === "viewed";
  const yardAddress = [
    yard.address?.line1,
    yard.address?.city,
    yard.address?.region,
    yard.address?.postalCode,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="pt-safe pb-safe min-h-dvh bg-ui-bg-subtle px-3 py-6 sm:py-12">
      <article className="mx-auto w-full max-w-[860px] overflow-hidden rounded-xl bg-ui-bg-base shadow-elevation-card-rest">
        <header className="flex flex-col gap-6 border-b border-ui-border-base p-6 sm:flex-row sm:items-start sm:justify-between sm:p-10">
          <div className="flex items-center gap-x-4">
            {yard.logoUrl ? (
              <img src={yard.logoUrl} alt="" className="size-14 rounded-lg object-contain" />
            ) : null}
            <div>
              <p className="text-[1.25rem] font-semibold leading-7 tracking-[-0.02em] text-ui-fg-base">
                {yard.name}
              </p>
              <p className="txt-small text-ui-fg-subtle">
                {[yardAddress, yard.phone].filter(Boolean).join(", ")}
              </p>
            </div>
          </div>
          <div className="sm:text-right">
            <p className="txt-compact-small text-ui-fg-muted">Quote</p>
            <p className="tabular text-[1.25rem] font-semibold leading-7 tracking-[-0.02em] text-ui-fg-base">
              #{quote.number}
            </p>
            <div className="mt-1.5 sm:flex sm:justify-end">
              {quote.status === "accepted" ? (
                <StatusBadge color="green">Accepted {date(quote.decidedAt)}</StatusBadge>
              ) : quote.status === "declined" ? (
                <StatusBadge color="red">Declined</StatusBadge>
              ) : quote.status === "expired" ? (
                <StatusBadge color="orange">Expired {date(quote.validUntil)}</StatusBadge>
              ) : (
                <StatusBadge color="blue">Valid until {date(quote.validUntil)}</StatusBadge>
              )}
            </div>
          </div>
        </header>

        <section className="grid gap-6 border-b border-ui-border-base p-6 sm:grid-cols-2 sm:p-10">
          <div>
            <p className="txt-compact-xsmall-plus mb-1 text-ui-fg-muted">Prepared for</p>
            <p className="txt-compact-medium-plus text-ui-fg-base">{quote.customer?.name ?? "-"}</p>
            {quote.customer?.company ? (
              <p className="txt-small text-ui-fg-subtle">{quote.customer.company}</p>
            ) : null}
          </div>
          <div>
            <p className="txt-compact-xsmall-plus mb-1 text-ui-fg-muted">Project</p>
            <p className="txt-compact-medium-plus text-ui-fg-base">{quote.projectName}</p>
            {quote.siteAddress?.line1 ? (
              <p className="txt-small text-ui-fg-subtle">{quote.siteAddress.line1}</p>
            ) : null}
          </div>
          {quote.notesToCustomer ? (
            <p className="txt-medium whitespace-pre-line text-ui-fg-subtle sm:col-span-2">
              {quote.notesToCustomer}
            </p>
          ) : null}
        </section>

        <section className="overflow-x-auto">
          <table className="txt-compact-small w-full text-ui-fg-subtle">
            <thead className="txt-compact-small-plus border-b border-ui-border-base bg-ui-bg-subtle text-left">
              <tr>
                <th className="py-2.5 pl-6 pr-3 font-medium sm:pl-10">Item</th>
                <th className="px-3 py-2.5 text-right font-medium">Qty</th>
                <th className="px-3 py-2.5 text-right font-medium max-sm:hidden">Price</th>
                <th className="py-2.5 pl-3 pr-6 text-right font-medium sm:pr-10">Total</th>
              </tr>
            </thead>
            <tbody>
              {sections.map(([section, lines]) => (
                <Fragment key={section}>
                  <tr className="border-b border-ui-border-base">
                    <td
                      colSpan={4}
                      className="txt-compact-xsmall-plus px-6 pb-1.5 pt-4 text-ui-fg-muted sm:px-10"
                    >
                      {sectionTitles[section] ?? section}
                    </td>
                  </tr>
                  {lines.map((line, index) => (
                    <tr key={index} className="border-b border-ui-border-base">
                      <td className="py-2.5 pl-6 pr-3 text-ui-fg-base sm:pl-10">
                        {line.description}
                      </td>
                      <td className="tabular whitespace-nowrap px-3 py-2.5 text-right">
                        {quantity(line.quantity)} {unitLabel(line.unit, line.quantity)}
                      </td>
                      <td className="tabular whitespace-nowrap px-3 py-2.5 text-right max-sm:hidden">
                        {money(line.unitPriceCents, quote.currency)}
                      </td>
                      <td className="tabular whitespace-nowrap py-2.5 pl-3 pr-6 text-right text-ui-fg-base sm:pr-10">
                        {money(line.totalCents, quote.currency)}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </section>

        <section className="flex justify-end border-b border-ui-border-base p-6 sm:p-10">
          <dl className="txt-compact-small flex w-full max-w-xs flex-col gap-y-2 text-ui-fg-subtle">
            <Total label="Materials" value={money(quote.subtotalCents, quote.currency)} />
            {quote.discountCents > 0 ? (
              <Total label="Discount" value={`-${money(quote.discountCents, quote.currency)}`} />
            ) : null}
            {quote.deliveryCents > 0 ? (
              <Total label="Delivery" value={money(quote.deliveryCents, quote.currency)} />
            ) : null}
            <Total
              label={`Tax (${bpsToPercent(quote.taxRateBps)}%)`}
              value={money(quote.taxCents, quote.currency)}
            />
            <div className="mt-2 flex items-baseline justify-between border-t border-ui-border-base pt-3">
              <dt className="txt-compact-medium-plus text-ui-fg-base">Total</dt>
              <dd className="tabular text-[1.5rem] font-semibold tracking-[-0.02em] text-ui-fg-base">
                {money(quote.totalCents, quote.currency)}
              </dd>
            </div>
          </dl>
        </section>

        {quote.terms ? (
          <section className="border-b border-ui-border-base p-6 sm:p-10">
            <p className="txt-compact-xsmall-plus mb-2 text-ui-fg-muted">Terms</p>
            <p className="txt-small whitespace-pre-line text-ui-fg-subtle">{quote.terms}</p>
          </section>
        ) : null}

        <footer className="flex flex-col-reverse gap-3 p-6 sm:flex-row sm:justify-end sm:p-10 print:hidden">
          <Button variant="secondary" size="large" asChild className="max-sm:w-full">
            <a href={`/api/v1/public/quotes/${token}/pdf`} download>
              Download PDF
            </a>
          </Button>
          {open ? (
            <>
              <Button
                variant="secondary"
                size="large"
                onClick={() => setConfirming("declined")}
                className="max-sm:w-full"
              >
                Decline
              </Button>
              <Button
                size="large"
                onClick={() => setConfirming("accepted")}
                className="max-sm:w-full"
              >
                Accept quote
              </Button>
            </>
          ) : null}
        </footer>
      </article>

      <p className="txt-compact-xsmall mt-6 text-center text-ui-fg-muted print:hidden">
        Questions about this quote? Contact {yard.name}
        {yard.email ? ` at ${yard.email}` : ""}.
      </p>

      <Prompt open={confirming !== null} onOpenChange={(next) => !next && setConfirming(null)}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>
              {confirming === "accepted" ? "Accept this quote?" : "Decline this quote?"}
            </Prompt.Title>
            <Prompt.Description>
              {confirming === "accepted"
                ? `You are accepting quote #${quote.number} from ${yard.name} for ${money(quote.totalCents, quote.currency)}. They will contact you to arrange the order.`
                : `${yard.name} will be told you have declined quote #${quote.number}.`}
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Go back</Prompt.Cancel>
            <Prompt.Action
              tone={confirming === "accepted" ? "primary" : "danger"}
              onClick={() => confirming && decide.mutate(confirming)}
            >
              {confirming === "accepted" ? "Accept quote" : "Decline quote"}
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </div>
  );
}

function Total({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-x-4">
      <dt>{label}</dt>
      <dd className="tabular text-ui-fg-base">{value}</dd>
    </div>
  );
}
